import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'routes';

import type { LandingPagePro } from 'modules/marketing-pages/marketing-page.types';
import { logEvent } from 'utils/analytics';
import { getUserDisplayName } from 'utils/tools';
import { getProLink } from 'utils/marketing-pages';

import styles from './ProBlogPost.module.scss';

export interface ProBlogPostProps {
  _id: string;
  content?: string;
  createdAt?: Date;
  htmlContent?: string;
  jobTypeId?: string;
  pro: {
    // from users collection, proProfile.customProfileUrl
    customProfileUrl?: string;
    id: string;
    profile: LandingPagePro['profile'];
    slug?: string;
  };
  title?: string;
}

interface Props {
  post: ProBlogPostProps;
}

const ProBlogPost: FC<Props> = ({
  post: {
    _id,
    jobTypeId = '',
    pro: { customProfileUrl, id, profile, slug },
    title = '',
  },
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const proDisplayName = useMemo(() => getUserDisplayName(language, profile), [language, profile]);
  const proLink = getProLink({
    customProfileUrl,
    language: language as 'en' | 'ja',
    proId: id,
    slug,
  });
  const proBlogLink = `${proLink}/blog/${_id}`;

  const handleGoogleAnalytics = useCallback(
    (eventType: string) => {
      logEvent(`ProBlogSection:${jobTypeId}`, `${eventType}`);
    },
    [jobTypeId]
  );

  const handleBlogTitleClick = useCallback(() => handleGoogleAnalytics('proBlogTitleClick'), [
    handleGoogleAnalytics,
  ]);

  return (
    <div className={styles['container']}>
      <div className={styles['title']}>
        <Link prefetch={false} route={proBlogLink}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <a onClick={handleBlogTitleClick}>{title}</a>
        </Link>
        <span className={styles['name']}>
          {t('marketingPages:proBlogPostSection.proName', { proDisplayName })}
        </span>
      </div>
    </div>
  );
};

export default ProBlogPost;
